import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { XIcon } from "lucide-react";
import Tooltip from "../../../../../components/shared/Tooltip";
import Item from "./Item";
import { useScheduleContext } from "../../hooks/context/useScheduleContext";
import { useFilters } from "../../../../../utils/hooks/useFilters";
import { useConfig } from "./useConfig";
import type { Resource } from "../../types/Resource";

export interface ActionBarProps {
    openActionsModal: (item: { ids: string[], action: string, type: string }) => void,
    openCopyModal: (item: { event: string, type: string, isTemplate: boolean } | string[]) => void,
    openTransferModal: (item: { type: string, eventId: string, userId: string | Resource, date: string, isTemplate: boolean }) => void,
    openDeleteModal: (item: { isTemplate: boolean, ids: string[], type: string }) => void,
}

function ActionBar({
    openActionsModal,
    openCopyModal,
    openTransferModal,
    openDeleteModal,
}: ActionBarProps) {
    const { t } = useTranslation();
    const { selectedEvents, selectedUnassignedEvents, ctrlShiftS, dispatch } = useScheduleContext();
    const [filters] = useFilters();
    const config = useConfig({
        openActionsModal,
        openCopyModal,
        openTransferModal,
        openDeleteModal
    });

    const deselectAll = useCallback(() => {
        dispatch({ type: "select-events", payload: [] });
        if (filters?.mode === "month") {
            dispatch({ type: "select-unassigned-events", payload: [] });
        }
    }, [dispatch, filters?.mode]);
    
    if (filters?.mode === "day" || !ctrlShiftS) return null;

    return (
        <div className="schedule-action-bar-container tw-p-4 tw-flex tw-items-center tw-gap-2 tw-border tw-border-border/50 tw-bg-white/90 tw-backdrop-blur-md tw-rounded-lg tw-flex-wrap tw-w-fit">
            <div className="tw-flex tw-items-center tw-flex-nowrap tw-border tw-border-neutral-200 tw-rounded-md tw-overflow-hidden">
                <div className="tw-py-2 tw-px-3 tw-text-sm tw-font-medium tw-text-neutral-700 tw-whitespace-nowrap">
                    {selectedEvents.length > 0
                        ? selectedEvents?.length
                        : selectedUnassignedEvents?.length
                    } {t("selected")}
                </div>

                <Tooltip
                    triggerClassName="tw-border-l tw-border-neutral-200 tw-rounded-none tw-border-t-0 tw-border-b-0 tw-border-r-0 tw-py-2 tw-px-3 tw-bg-transparent tw-shadow-none tw-text-neutral-400 tw-cursor-pointer hover:tw-bg-neutral-50 hover:tw-text-neutral-700 tw-transition-colors"
                    trigger={(
                        <div onClick={deselectAll}>
                            <XIcon width={14} />
                        </div>
                    )}
                >
                    {t("deselect-all")}
                </Tooltip>
            </div>

            <div className="tw-w-px tw-h-6 tw-bg-neutral-200" />

            <div className="tw-flex tw-items-center tw-gap-1.5">
                {config.map((item: any) => {
                    return <Item key={item.id} {...item} />
                })}
            </div>
        </div>
    )
}

export default ActionBar;
